/**
 * 行动项节点视图组件
 * 用于渲染 Lexical ActionItemNode
 */

import { Circle, Clock, User } from "lucide-react";
import React from "react";
import type { ActionItemData } from "@/components/Editor/nodes/types";
import { cn } from "@/lib/utils";
import { useLocale } from "@/utils/i18n";

interface ActionItemViewProps {
  data: ActionItemData;
  className?: string;
}

const statusConfig = {
  todo: {
    label: "待办",
    icon: Circle,
    color: "text-gray-600 dark:text-gray-400",
    bg: "bg-gray-100 dark:bg-gray-800",
    border: "border-gray-300 dark:border-gray-600",
  },
  "in-progress": {
    label: "进行中",
    icon: Clock,
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-100 dark:bg-blue-900/30",
    border: "border-blue-300 dark:border-blue-700",
  },
  done: {
    label: "已完成",
    icon: Circle,
    color: "text-green-600 dark:text-green-400",
    bg: "bg-green-100 dark:bg-green-900/30",
    border: "border-green-300 dark:border-green-700",
  },
};

const priorityConfig = {
  high: {
    label: "高优先级",
    color: "text-red-600 dark:text-red-400",
    dot: "bg-red-500",
  },
  medium: {
    label: "中优先级",
    color: "text-yellow-600 dark:text-yellow-400",
    dot: "bg-yellow-500",
  },
  low: {
    label: "低优先级",
    color: "text-green-600 dark:text-green-400",
    dot: "bg-green-500",
  },
};

export const ActionItemView: React.FC<ActionItemViewProps> = ({ data, className }) => {
  const locale = useLocale();
  const { action, priority, status, dueDate, assignee, notes } = data;
  const statusCfg = statusConfig[status];
  const priorityCfg = priorityConfig[priority];

  // 格式化日期
  const formattedDueDate = React.useMemo(() => {
    if (!dueDate) return null;
    try {
      const date = new Date(dueDate);
      return date.toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dueDate;
    }
  }, [dueDate, locale]);

  // 检查是否过期
  const isOverdue = React.useMemo(() => {
    if (!dueDate || status === "done") return false;
    try {
      const due = new Date(dueDate);
      return due < new Date();
    } catch {
      return false;
    }
  }, [dueDate, status]);

  return (
    <div className={cn("my-4 p-4 rounded-lg border bg-card", "action-item-node", status === "done" && "opacity-75", className)}>
      {/* 顶部信息栏 */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 flex-1">
          {/* 优先级指示器 */}
          <div className={cn("w-2 h-2 rounded-full", priorityCfg.dot)} />

          {/* 行动描述 */}
          <div className={cn("text-base font-medium", status === "done" && "line-through")}>{action}</div>
        </div>

        {/* 状态标签 */}
        <span
          className={cn(
            "px-2 py-1 text-xs font-medium rounded-md border flex-shrink-0 ml-2",
            statusCfg.color,
            statusCfg.bg,
            statusCfg.border,
          )}
        >
          {statusCfg.label}
        </span>
      </div>

      {/* 优先级和元数据 */}
      <div className="flex flex-wrap items-center gap-3 text-sm mb-3">
        {/* 优先级 */}
        <div className={cn("font-medium flex items-center gap-1", priorityCfg.color)}>
          <span className={cn("w-1.5 h-1.5 rounded-full", priorityCfg.dot)} />
          {priorityCfg.label}
        </div>

        {/* 负责人 */}
        {assignee && (
          <div className="flex items-center gap-1 text-muted-foreground">
            <User className="w-4 h-4" />
            <span>{assignee}</span>
          </div>
        )}

        {/* 截止日期 */}
        {formattedDueDate && (
          <div
            className={cn("flex items-center gap-1", isOverdue ? "text-red-600 dark:text-red-400 font-medium" : "text-muted-foreground")}
          >
            <Clock className="w-4 h-4" />
            <span>{formattedDueDate}</span>
            {isOverdue && <span className="text-xs">(已逾期)</span>}
          </div>
        )}
      </div>

      {/* 备注 */}
      {notes && (
        <div className="mt-3 pt-3 border-t text-sm text-muted-foreground">
          <div className="font-medium mb-1">备注:</div>
          <div>{notes}</div>
        </div>
      )}
    </div>
  );
};

export default ActionItemView;
